import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  InputAdornment,
  Paper,
  Stack,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { teachingValidators } from "../../teaching-validators";

export const TeachingFormFields = ({
  formValues,
  formValueErrors,
  onFormValuesChange,
  onFormValueErrorsChange,
  isSubmitting = false,
  showDescriptionValidation = true,
}) => {
  const intl = useIntl();

  const handlePriceFieldChange = (event) => {
    const newPriceValue = event.target.value;
    onFormValuesChange({ ...formValues, price: newPriceValue });

    try {
      teachingValidators.validatePrice(newPriceValue, intl);
      onFormValueErrorsChange({ ...formValueErrors, price: false });
    } catch (err) {
      onFormValueErrorsChange({ ...formValueErrors, price: err.message });
    }
  };

  const handleAcceptsRemoteSwitchChange = (event) => {
    onFormValuesChange({ ...formValues, acceptsRemote: event.target.checked });
  };

  const handleDescriptionChange = (event) => {
    const newDescription = event.target.value;
    onFormValuesChange({ ...formValues, description: newDescription });
    
    if (showDescriptionValidation) {
      onFormValueErrorsChange({ 
        ...formValueErrors, 
        description: newDescription.length < 150
      });
    }
  };

  const acceptRemoteLabel = intl.formatMessage({
    id: "teacher.general.accept-remote-label",
  });

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* Price Input */}
      <Box>
        <Typography variant="h6" sx={{ mb: { xs: 1.5, md: 2 }, fontWeight: 600 }}>
          <FormattedMessage id="teacher.general.price" />
        </Typography>
        <FormControl fullWidth>
          <TextField
            value={formValues.price}
            error={!!formValueErrors.price}
            label={<FormattedMessage id="teacher.general.price" />}
            helperText={formValueErrors.price}
            onChange={handlePriceFieldChange}
            disabled={isSubmitting}
            inputProps={{
              'data-testid': 'teaching-price-input',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>€</Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </FormControl>
      </Box>

      {/* Remote Switch */}
      <Box>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: formValues.acceptsRemote ? 'primary.50' : 'background.paper',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formValues.acceptsRemote}
                color="primary"
                onChange={handleAcceptsRemoteSwitchChange}
                disabled={isSubmitting}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {acceptRemoteLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="teacher.remote.description" defaultMessage="Accept remote teaching sessions" />
                </Typography>
              </Box>
            }
          />
        </Paper>
      </Box>

      {/* Description */}
      <Box>
        <Typography variant="h6" sx={{ mb: { xs: 1.5, md: 2 }, fontWeight: 600 }}>
          <FormattedMessage id="teacher.description" defaultMessage="Description" />
        </Typography>
        <FormControl fullWidth>
          <TextField
            value={formValues.description}
            error={!!formValueErrors.description}
            onChange={handleDescriptionChange}
            disabled={isSubmitting}
            multiline
            minRows={10}
            placeholder={intl.formatMessage({
              id: "teaching-description-placeholder",
              defaultMessage: "Describe your teaching approach, experience, and what students can expect..."
            })}
            helperText={
              showDescriptionValidation ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="caption" 
                    color={formValues.description.length < 150 ? 'error' : 'text.secondary'}
                    sx={{ 
                      fontWeight: formValues.description.length < 150 ? 500 : 400,
                      fontSize: '0.75rem'
                    }}
                  >
                    {formValues.description.length} <FormattedMessage id="teacher.description.characters" />
                    {formValues.description.length < 150 ? (
                      <span> (<FormattedMessage id="teacher.description.minimum-required" values={{ minLength: 150 }} />)</span>
                    ) : (
                      <span> <FormattedMessage id="teacher.description.valid" /></span>
                    )}
                  </Typography>
                </Box>
              ) : undefined
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: formValueErrors.description ? 'error.main' : 'primary.main',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: formValueErrors.description ? 'error.main' : 'divider',
              },
              '& .MuiInputBase-root': {
                backgroundColor: formValueErrors.description ? 'error.50' : 'background.paper',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        </FormControl>
      </Box>
    </Stack>
  );
};

TeachingFormFields.propTypes = {
  formValues: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    acceptsRemote: PropTypes.bool.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  formValueErrors: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    acceptsRemote: PropTypes.bool.isRequired,
    description: PropTypes.bool.isRequired,
  }).isRequired,
  onFormValuesChange: PropTypes.func.isRequired,
  onFormValueErrorsChange: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  showDescriptionValidation: PropTypes.bool,
};
